// @flow
import type { SeatingFromServer, QuickSeatingExtendedOptions } from "./types"

export const SEATING_LOADING = "SEATING_LOADING"
export const SEATING_EMPTY = "SEATING_EMPTY"
export const SEATING_ERROR = "SEATING_ERROR"
export const SET_SEATING_DATA = "SET_SEATING_DATA"
export const CHANGE_QUICK_SEATING_OPTION = "CHANGE_QUICK_SEATING_OPTION"
export const CHANGE_SEAT_MAP_OPTION = "CHANGE_SEAT_MAP_OPTION"
export const SEATING_REQUEST_STATUS = "SEATING_REQUEST_STATUS"

export type SeatingLoadingAction = {
  type: "SEATING_LOADING",
}

export type SeatingEmptyAction = {
  type: "SEATING_EMPTY",
}

export type SeatingErrorAction = {
  type: "SEATING_ERROR",
  payload: {
    error: Error,
  },
}

export type SeatingRequestStatus = "initial" | "valid" | "loading" | "error"

export type SeatingRequestStatusAction = {
  type: "SEATING_REQUEST_STATUS",
  payload: {
    status: SeatingRequestStatus,
  },
}

export type SetSeatingAction = {
  type: "SET_SEATING_DATA",
  payload: SeatingFromServer,
}

export type ChangeQuickSeatingOptionAction = {
  type: "CHANGE_QUICK_SEATING_OPTION",
  payload: { selectedOption: QuickSeatingExtendedOptions },
  meta: { segmentCode: string },
}

export type ChangeSeatMapOptionAction = {
  type: "CHANGE_SEAT_MAP_OPTION",
  payload: { selectedSeat: string },
  meta: { segmentCode: string, forPassenger: number },
}

export type ActionTypes =
  | SetSeatingAction
  | ChangeQuickSeatingOptionAction
  | ChangeSeatMapOptionAction
  | SeatingLoadingAction
  | SeatingErrorAction
  | SeatingEmptyAction
  | SeatingRequestStatusAction

/**
 * Action creator for registering seating data is being loaded
 */
export const seatingLoading = (): SeatingLoadingAction => ({
  type: SEATING_LOADING,
})

/**
 * Action creator for registering seating data is being loaded
 */
export const seatingEmpty = (): SeatingEmptyAction => ({
  type: SEATING_EMPTY,
})

/**
 * Action creator for registering seating data is being loaded
 */
export const seatingRequestStatus = (status: SeatingRequestStatus): SeatingRequestStatusAction => ({
  type: SEATING_REQUEST_STATUS,
  payload: {
    status,
  },
})

/**
 * Action creator for registering error in seating data response
 */
export const seatingError = (error: Error): SeatingErrorAction => ({
  type: SEATING_ERROR,
  payload: {
    error,
  },
})

/**
 * Action creator for setting data from check_ancillaries API to state
 * @param {SeatingFromServer} payload - payload from the server
 */
export const setSeatingData = (payload: SeatingFromServer): SetSeatingAction => ({
  type: SET_SEATING_DATA,
  payload,
})

/**
 * Action creator for changing selected quick seating options for a specific segment
 * @param {string} segmentCode - code for the segment seating is being changed for
 * @param {QuickSeatingExtendedOptions} selectedOption - new quick option selected by the user in camelCase
 */
export const changeQuickSeatingOption = (
  segmentCode: string,
  selectedOption: QuickSeatingExtendedOptions,
): ChangeQuickSeatingOptionAction => ({
  type: CHANGE_QUICK_SEATING_OPTION,
  payload: { selectedOption },
  meta: { segmentCode },
})

/**
 * Action creator for changing selected seating map option for a specific segment
 * @param {string} segmentCode - code for the segment seating is being changed for
 * @param {number} forPassenger - index of passenger seat was selected for
 * @param {string} selectedSeat - seats user selected
 */
export const changeSeatMapSeatingOption = (
  segmentCode: string,
  forPassenger: number,
  selectedSeat: string,
): ChangeSeatMapOptionAction => ({
  type: CHANGE_SEAT_MAP_OPTION,
  payload: { selectedSeat },
  meta: { segmentCode, forPassenger },
})
